/* eslint-disable react/jsx-props-no-spreading */
import { format } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import {
  Autocomplete as FormikAutocomplete,
  Select as FormikSelect,
  TextField as FormikTextField,
} from 'formik-mui';
import { DatePicker as MuiDatePicker } from 'formik-mui-x-date-pickers';
import snakecaseKeys from 'snakecase-keys';
import * as Yup from 'yup';

import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import React from 'react';

import { toast } from 'react-toastify';

import { getClinicBranches } from '@api/clinic-branches';
import { generateFinancialReports } from '@api/financial-reports';
import { getOrganisations } from '@api/organisations';

import Tooltip from '@components/tooltip/Tooltip';

import { useAuth } from '@contexts/authContext';

import {
  downloadFile,
  parseJsonFromBlob,
  renameFileFromContentDisposition,
} from '@utils';

const professionsOptions = ['doctor', 'clinical assistant', 'nurse'];

const includeSlotTypesOptions = ['completed', 'confirmed'];

const reportFormatOptions = ['json', 'xlsx'];

const validationSchema = Yup.object({
  startDate: Yup.date()
    .nullable()
    .max(Yup.ref('endDate'), 'Start Date cannot be after End Date')
    .required('Required'),
  endDate: Yup.date()
    .nullable()
    .min(Yup.ref('startDate'), 'End Date cannot be before Start Date')
    .required('Required'),
  clinicIds: Yup.array().of(
    Yup.object().shape({ id: Yup.string(), title: Yup.string() }),
  ),
  clinicGroupIds: Yup.array().of(
    Yup.object().shape({ id: Yup.string(), title: Yup.string() }),
  ),
  professions: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one profession')
    .required('Required'),
  includeSlotTypes: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one slot type')
    .required('Required'),
  reportFormat: Yup.string().oneOf(reportFormatOptions).required('Required'),
  page: Yup.number().integer('Must be an integer'),
  limit: Yup.number().integer('Must be an integer'),
}).test(
  'clinic-selection',
  'You must select either Clinic(s) or Clinic Group(s), but not both.',
  function (values) {
    const { clinicIds, clinicGroupIds } = values;
    const hasClinicIds = clinicIds && clinicIds.length > 0;
    const hasClinicGroupIds = clinicGroupIds && clinicGroupIds.length > 0;

    if (hasClinicIds && hasClinicGroupIds) {
      return (
        this.createError({
          path: 'clinicIds',
          message:
            'You can only select either Clinics or Clinic Groups, not both.',
        }) &&
        this.createError({
          path: 'clinicGroupIds',
          message:
            'You can only select either Clinics or Clinic Groups, not both.',
        })
      );
    }

    if (!hasClinicIds && !hasClinicGroupIds) {
      return (
        this.createError({
          path: 'clinicIds',
          message: 'You must select at least one Clinic or Clinic Group.',
        }) &&
        this.createError({
          path: 'clinicGroupIds',
          message: 'You must select at least one Clinic or Clinic Group.',
        })
      );
    }

    return true;
  },
);

function TooltipLimit() {
  return (
    <Tooltip
      placement='right'
      content={
        <Box
          sx={{
            padding: '0.5rem',
            boxShadow:
              '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            backgroundColor: '#f5f5f9',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: '0.75rem',
            border: '1px solid #dadde9',
            borderRadius: '4px',
          }}
        >
          <b>Limit</b>
          <p>Limit number of row(s) to be generated. Set to 0 for all data.</p>
        </Box>
      }
    >
      <HelpOutlineRoundedIcon
        sx={{ fontSize: '0.9rem', margin: '4px 4px 16px 4px' }}
        placement='right'
      />
    </Tooltip>
  );
}

function DownloadFinancialReports(_props) {
  const initialValues = {
    startDate: null,
    endDate: null,
    clinicIds: [],
    clinicGroupIds: [],
    professions: [],
    includeSlotTypes: ['completed'],
    reportFormat: 'xlsx',
    page: 1,
    limit: 1000,
  };
  const { auth } = useAuth();
  const [organisationsData, setOrganisationsData] = React.useState([]);
  const [clinicsData, setClinicsData] = React.useState([]);
  const clinicGroups = React.useMemo(
    () => [
      { id: 'all', title: 'All' },
      ...organisationsData.map((it) => ({
        id: it._id,
        title: it.business_name,
      })),
    ],
    [organisationsData],
  );

  const clinics = React.useMemo(
    () => [
      { id: 'all', title: 'All' },
      ...clinicsData.map((it) => ({ id: it._id, title: it.clinicName })),
    ],
    [clinicsData],
  );

  React.useEffect(() => {
    if (auth?.idToken) {
      (async () => {
        try {
          const res = await getOrganisations({
            token: auth.idToken,
            params: { status: 'verified' },
          });
          if (
            res.status >= 200 &&
            res.status < 300 &&
            Array.isArray(res.data)
          ) {
            setOrganisationsData(res.data);
          }
        } catch (err) {
          toast.error(err.message, {
            position: 'top-center',
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      })();

      (async () => {
        try {
          const res = await getClinicBranches({
            token: auth.idToken,
          });
          if (
            res.status >= 200 &&
            res.status < 300 &&
            Array.isArray(res.data)
          ) {
            setClinicsData(res.data);
          }
        } catch (err) {
          toast.error(err.message, {
            position: 'top-center',
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
      })();
    }
  }, [auth]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const filter = {
      startDate: values.startDate
        ? format(values.startDate, 'yyyy-MM-dd')
        : undefined,
      endDate: values.endDate
        ? format(values.endDate, 'yyyy-MM-dd')
        : undefined,
      clinicIds:
        values.clinicIds.length > 0
          ? values.clinicIds.map((c) => c.id).join(',')
          : undefined,
      clinicGroupIds:
        values.clinicGroupIds.length > 0
          ? values.clinicGroupIds.map((cg) => cg.id).join(',')
          : undefined,
      professions: values.professions.join(','),
      includeSlotTypes: values.includeSlotTypes.join(','),
      reportFormat: values.reportFormat || 'xlsx',
    };
    const pagination = {
      page:
        typeof values.page === 'number' && values.page <= 0 ? 1 : values.page,
      limit:
        typeof values.limit === 'number' && values.limit < 0
          ? 1000
          : values.limit,
    };

    if (pagination.limit === 0 && pagination.page !== 1) {
      pagination.page = 1;
    }

    if (values.clinicIds.length > 0) {
      filter.clinicGroupIds = undefined;
    } else if (values.clinicGroupIds.length > 0) {
      filter.clinicIds = undefined;
    } else {
      filter.clinicGroupIds = undefined;
      filter.clinicIds = undefined;
    }

    const payload = snakecaseKeys({ filter, pagination }, { deep: true });

    try {
      setSubmitting(true);
      const res = await generateFinancialReports({
        token: auth.idToken,
        payload,
        config: {
          responseType: 'blob',
        },
      });
      if (res.status >= 200 && res.status < 300 && res.data) {
        const buffer = renameFileFromContentDisposition(res.data, res.headers);
        downloadFile(buffer);
        return;
      }

      toast.error(
        `Error in generating file: invalid response data (${res.status})`,
        {
          position: 'top-center',
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    } catch (err) {
      const data = await parseJsonFromBlob(err.data || err.response?.data);
      toast.error(data?.message || err.message, {
        position: 'top-center',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    setSubmitting(false);
  };

  return (
    <Card
      sx={{ p: 2, mt: 3, ml: 3, pt: 3, mb: 3, width: '70%', borderRadius: 4 }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            setFieldValue,
            touched,
            errors,
            isSubmitting,
            isValid,
            dirty,
          }) => {
            return (
              <Form>
                <Box sx={{ p: 3, maxWidth: 960 }}>
                  <Grid container spacing={6}>
                    <Grid item xs={12} sm={6}>
                      {/* Start Date */}
                      <Field
                        component={MuiDatePicker}
                        label='Start Date'
                        name='startDate'
                        textField={{ helperText: 'Select start date' }}
                        sx={{ mb: 2, width: '100%' }}
                        fullWidth
                        maxDate={values.endDate || null}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />

                      {/* End Date */}
                      <Field
                        component={MuiDatePicker}
                        label='End Date'
                        name='endDate'
                        textField={{ helperText: 'Select end date' }}
                        sx={{ mb: 2, width: '100%' }}
                        fullWidth
                        minDate={values.startDate || null}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />

                      {/* Clinic Group IDs */}
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Field
                          component={FormikAutocomplete}
                          multiple
                          id='clinic-group-ids'
                          name='clinicGroupIds'
                          options={clinicGroups}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.title}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          onChange={(_event, newValue) => {
                            const hasAll = newValue.some(
                              (opt) => opt.id === 'all',
                            );
                            if (hasAll) {
                              setFieldValue('clinicGroupIds', [
                                { id: 'all', title: 'All' },
                              ]);
                              setFieldValue('clinicIds', []);
                            } else {
                              const filteredNewValue = newValue.filter(
                                (opt) => opt.id !== 'all',
                              );
                              setFieldValue('clinicGroupIds', filteredNewValue);
                              if (values.clinicIds.length > 0) {
                                setFieldValue('clinicIds', []);
                              }
                            }
                          }}
                          renderOption={(props, option) => {
                            const allSelected = values.clinicGroupIds.some(
                              (opt) => opt.id === 'all',
                            );
                            const isChecked = values.clinicGroupIds.some(
                              (item) => item.id === option.id,
                            );
                            const isDisabled =
                              allSelected && option.id !== 'all';
                            return (
                              <li
                                {...props}
                                style={{ opacity: isDisabled ? 0.5 : 1 }}
                              >
                                <Checkbox
                                  style={{ marginRight: 8 }}
                                  checked={isChecked}
                                  disabled={isDisabled}
                                />
                                <ListItemText primary={option.title} />
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name='clinicGroupIds'
                              error={
                                touched['clinicGroupIds'] &&
                                !!errors['clinicGroupIds']
                              }
                              helperText={errors['clinicGroupIds']}
                              label='Clinic Group(s)'
                              placeholder='Select Clinic Groups'
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.title}
                                {...getTagProps({ index })}
                                key={option.id}
                              />
                            ))
                          }
                        />
                      </FormControl>

                      {/* Clinic IDs */}
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Field
                          component={FormikAutocomplete}
                          multiple
                          id='clinic-ids'
                          name='clinicIds'
                          options={clinics}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.title}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          onChange={(_event, newValue) => {
                            const hasAll = newValue.some(
                              (opt) => opt.id === 'all',
                            );
                            if (hasAll) {
                              setFieldValue('clinicIds', [
                                { id: 'all', title: 'All' },
                              ]);
                              setFieldValue('clinicGroupIds', []);
                            } else {
                              const filteredNewValue = newValue.filter(
                                (opt) => opt.id !== 'all',
                              );
                              setFieldValue('clinicIds', filteredNewValue);
                              if (values.clinicGroupIds.length > 0) {
                                setFieldValue('clinicGroupIds', []);
                              }
                            }
                          }}
                          renderOption={(props, option) => {
                            const allSelected = values.clinicIds.some(
                              (opt) => opt.id === 'all',
                            );
                            const isChecked = values.clinicIds.some(
                              (item) => item.id === option.id,
                            );
                            const isDisabled =
                              allSelected && option.id !== 'all';
                            return (
                              <li
                                {...props}
                                style={{ opacity: isDisabled ? 0.5 : 1 }}
                              >
                                <Checkbox
                                  style={{ marginRight: 8 }}
                                  checked={isChecked}
                                  disabled={isDisabled}
                                />
                                <ListItemText primary={option.title} />
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name='clinicIds'
                              error={
                                touched['clinicIds'] && !!errors['clinicIds']
                              }
                              helperText={errors['clinicIds']}
                              label='Clinic(s)'
                              placeholder='Select Clinics'
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.title}
                                {...getTagProps({ index })}
                                key={option.id}
                              />
                            ))
                          }
                        />
                      </FormControl>

                      {values.clinicIds.length > 0 &&
                        values.clinicGroupIds.length > 0 && (
                          <Alert severity='warning' sx={{ mb: 2 }}>
                            You can only select either Clinics or Clinic Groups,
                            not both. Selecting one will clear the other.
                          </Alert>
                        )}

                      {/* Professions */}
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Field
                          component={FormikSelect}
                          labelId='professions-label'
                          id='professions-select'
                          multiple
                          name='professions'
                          label='Professions'
                          input={
                            <OutlinedInput
                              id='select-multiple-chip'
                              label='Professions'
                            />
                          }
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  sx={{
                                    borderRadius: '4px',
                                    textTransform: 'capitalize',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {professionsOptions.map((name) => (
                            <MenuItem key={name} value={name}>
                              <Checkbox
                                checked={values.professions.indexOf(name) > -1}
                              />
                              <ListItemText
                                primary={name}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                      sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                      {/* Include Slot Types */}
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <Field
                          component={FormikSelect}
                          multiple
                          labelId='include-slot-types-label'
                          id='include-slot-types-select'
                          name='includeSlotTypes'
                          label='Include Slot Type'
                          input={<OutlinedInput label='Include Slot Type' />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  sx={{
                                    borderRadius: '4px',
                                    textTransform: 'capitalize',
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {includeSlotTypesOptions.map((name) => (
                            <MenuItem key={name} value={name}>
                              <Checkbox
                                checked={
                                  values.includeSlotTypes.indexOf(name) > -1
                                }
                              />
                              <ListItemText
                                primary={name}
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>

                      {/* Report Format */}
                      {/* <FormControl fullWidth sx={{ mb: 2 }}>
                      {!values.reportFormat && (
                        <InputLabel id='report-format-label'>
                          Select Report Format
                        </InputLabel>
                      )}
                      <Field
                        component={FormikSelect}
                        labelId='report-format-label'
                        id='report-format-select'
                        name='reportFormat'
                        label='Report Format'
                        input={<OutlinedInput label='Report Format' />}
                      >
                        {reportFormatOptions.map((format) => (
                          <MenuItem key={format} value={format}>
                            <ListItemText
                              primary={format}
                              sx={{ textTransform: 'uppercase' }}
                            />
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl> */}

                      {/* Page */}
                      <Field
                        component={FormikTextField}
                        fullWidth
                        label={values.page ? 'Page' : 'Page (1)'}
                        placeholder='1'
                        name='page'
                        type='number'
                        inputProps={{ min: 1 }}
                        sx={{ mb: 2 }}
                      />

                      {/* Limit */}
                      <Field
                        component={FormikTextField}
                        fullWidth
                        label={
                          values.limit ? (
                            'Limit'
                          ) : (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box>Limit (1000)</Box>
                              <TooltipLimit />
                            </Box>
                          )
                        }
                        placeholder='1000'
                        name='limit'
                        type='number'
                        inputProps={{ min: 0 }}
                        sx={{ mb: 3 }}
                      />

                      <Button
                        type='submit'
                        variant='contained'
                        // color='primary'
                        className='bg-cyan-600'
                        fullWidth
                        disabled={isSubmitting || !isValid || !dirty}
                        sx={{
                          mt: 2,
                          py: 2,
                          fontSize: '1.1rem',
                          textTransform: 'capitalize',
                        }}
                      >
                        {isSubmitting ? 'Generating...' : 'Generate Report'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </LocalizationProvider>
    </Card>
  );
}

export default DownloadFinancialReports;
